// src/pages/subscription/SubscriptionSuccess.tsx

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, PartyPopper } from 'lucide-react';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../contexts/AuthContext';

const PAYMENT_TOKEN_KEY = 'orlo_payment_session_token';
const PAYMENT_CHECKOUT_KEY = 'orlo_payment_checkout_id';

function maskToken(token: string | null): string {
  if (!token || token.length <= 8) return '****';
  return `${token.slice(0, 4)}****`;
}

function readSessionParam(urlParam: string, storageKey: string): string | null {
  const params = new URLSearchParams(window.location.search);
  const value = params.get(urlParam);
  if (value) sessionStorage.setItem(storageKey, value);
  return value ?? sessionStorage.getItem(storageKey);
}

const SubscriptionSuccess: React.FC = () => {
  const { currentUser } = useAuth();
  const { display, refreshStatus } = useSubscription(currentUser);

  const [sessionToken] = useState<string | null>(() =>
    readSessionParam('customer_session_token', PAYMENT_TOKEN_KEY)
  );
  const [checkoutId] = useState<string | null>(() =>
    readSessionParam('checkout_id', PAYMENT_CHECKOUT_KEY)
  );

  const isFromPayment = !!(sessionToken || checkoutId);

  // token 存入 sessionStorage 後立即清除 URL，防止留存於瀏覽器歷史與 Referrer header
  useEffect(() => {
    if (sessionToken || checkoutId) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // 付款處理狀態
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'confirmed' | 'timeout'>(
    isFromPayment ? 'processing' : 'confirmed'
  );
  const [timeoutCountdown, setTimeoutCountdown] = useState(60); // 60秒超時
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);

  // 穩定的檢查函數，避免依賴項變化
  const checkPaymentStatus = useCallback(() => {
    if (display?.planDisplay === 'Pro' && display?.rightText === 'Active') {
      setPaymentStatus('confirmed');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return true; // 成功
    }
    return false; // 繼續等待
  }, [display?.planDisplay, display?.rightText]);


  // 輪詢邏輯 - 只在必要時啟動
  useEffect(() => {
    if (!isFromPayment || paymentStatus !== 'processing') return;

    const maxPollCount = 30; // 最多輪詢30次 (60秒)

    const startPolling = async () => {
      // 立即檢查一次
      await refreshStatus();

      // 設置定時器
      intervalRef.current = setInterval(async () => {
        pollCountRef.current++;
        setTimeoutCountdown(60 - (pollCountRef.current * 2));

        await refreshStatus();

        // 超時處理
        if (pollCountRef.current >= maxPollCount) {
          setPaymentStatus('timeout');
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
        }
      }, 2000);
    };

    startPolling();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isFromPayment, paymentStatus, refreshStatus]); // 移除不穩定的依賴

  // 分離的狀態檢查 - 當 display 變化時檢查
  useEffect(() => {
    if (paymentStatus === 'processing') {
      checkPaymentStatus();
    }
  }, [display, paymentStatus, checkPaymentStatus]);


  // 首次刷新
  useEffect(() => {
    const refreshTimer = setTimeout(async () => {
      await refreshStatus();
    }, isFromPayment ? 2000 : 1000); // 從付款回來的延遲更長

    return () => clearTimeout(refreshTimer);
  }, [refreshStatus, isFromPayment]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          
          {/* Success Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
          </div>

          {/* Content */}
          <div className="text-center">
            {paymentStatus === 'processing' && (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  Processing your payment...
                </h1>

                <p className="text-gray-600 mb-6">
                  Please wait while we confirm your subscription. This usually takes a few seconds.
                </p>

                {/* Processing indicator */}
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-center space-x-3">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-sky-600"></div>
                    <span className="text-sky-800 font-medium">
                      Confirming payment... ({timeoutCountdown}s)
                    </span>
                  </div>
                </div>
              </>
            )}

            {paymentStatus === 'confirmed' && (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  <span className="inline-flex items-center gap-2">
                    Welcome to Pro! <PartyPopper className="w-6 h-6 text-yellow-500" />
                  </span>
                </h1>

                <p className="text-gray-600 mb-6">
                  Your subscription is now active and you have unlimited access to all Pro features.
                </p>

                {/* Subscription Status */}
                {display && display.planDisplay === 'Pro' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex items-center justify-center">
                      <span className="text-green-800 font-medium flex items-center gap-1">
                        <CheckCircle className="w-4 h-4" />
                        {display.planDisplay} {display.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Plan Active
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}

            {paymentStatus === 'timeout' && (
              <>
                <h1 className="text-2xl font-bold text-gray-900 mb-4">
                  <span className="inline-flex items-center gap-2">
                    Payment Received <CheckCircle className="w-6 h-6 text-green-500" />
                  </span>
                </h1>

                <p className="text-gray-600 mb-4">
                  Your payment was successful, but Pro features are still activating.
                </p>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="text-yellow-800">
                    <p className="font-medium mb-2">Activation in progress</p>
                    <p className="text-sm">
                      If Pro features don't activate within 5 minutes, please contact support at{' '}
                      <a href="mailto:support@orlo.cc" className="text-yellow-700 underline">
                        support@orlo.cc
                      </a>
                    </p>
                  </div>
                </div>
              </>
            )}

            {/* Features Reminder */}
            <div className="bg-sky-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-sky-900 mb-2">Your Pro features:</h3>
              <ul className="text-sky-800 text-sm space-y-1">
                <li>• Unlimited smart task planning</li>
                <li>• Advanced day summaries and insights</li>
                <li>• Smart capture-to-task conversion</li>
                <li>• Priority email support</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {paymentStatus === 'confirmed' && (
                <>
                  <Link
                    to="/today"
                    className="w-full inline-flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    Start Using Pro Features
                  </Link>

                  <Link
                    to="/profile/subscription"
                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    Manage Subscription
                  </Link>
                </>
              )}

              {(paymentStatus === 'processing' || paymentStatus === 'timeout') && (
                <>
                  <button
                    onClick={refreshStatus}
                    className="w-full inline-flex justify-center py-3 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    Check Status Again
                  </button>

                  <Link
                    to="/today"
                    className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500"
                  >
                    Continue to App
                  </Link>

                  {paymentStatus === 'timeout' && (
                    <a
                      href={`mailto:support@orlo.cc?subject=Pro%20Features%20Activation%20Issue&body=Hi%20Orlo%20support%2C%0A%0AI%20completed%20payment%20but%20my%20Pro%20features%20haven%27t%20activated%20yet.%0A%0APayment%20completed%20at%3A%20${encodeURIComponent(new Date().toISOString())}%0A%0APlease%20help%20activate%20my%20Pro%20subscription.%0A%0AThanks%21`}
                      className="w-full inline-flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                      Contact Support
                    </a>
                  )}
                </>
              )}
            </div>

            {/* Receipt Info */}
            {(sessionToken || checkoutId) && (
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500">
                  {checkoutId ? `Checkout ID: ${maskToken(checkoutId)}` : `Session Token: ${maskToken(sessionToken)}`}
                </p>
                {paymentStatus === 'confirmed' && (
                  <p className="text-xs text-gray-500 mt-1">
                    A receipt has been sent to your email address.
                  </p>
                )}
                {paymentStatus === 'timeout' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Please include this ID when contacting support.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Support Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Questions? Contact us at{' '}
            <a 
              href="mailto:support@orlo.cc" 
              className="text-sky-600 hover:text-sky-500"
            >
              support@orlo.cc
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccess;